import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
    MapPin,
    Search,
    Phone,
    Clock,
    Globe,
    Filter,
    Loader2,
    XCircle,
    Compass,
} from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { WhatsAppButton } from "../components/site/WhatsAppButton";
import { apiClient } from "../lib/api";
import { brandIcon } from "../lib/leafletSetup";
import {
    LOCATION_TYPE_META,
    LOCATION_TYPE_OPTIONS,
    LINE_META,
    LINE_OPTIONS,
} from "../lib/locations";

// Default centre — middle of Portugal, fairly zoomed out so most points fit.
const DEFAULT_CENTER = [39.5, -8.0];
const DEFAULT_ZOOM = 7;

// Imperatively re-fits the map view from outside the MapContainer.
function MapController({ flyTo }) {
    const map = useMap();
    useEffect(() => {
        if (flyTo) {
            map.flyTo([flyTo.lat, flyTo.lng], 15, { duration: 0.7 });
        }
    }, [flyTo, map]);
    return null;
}

function FitToBounds({ locations }) {
    const map = useMap();
    useEffect(() => {
        if (!locations || locations.length === 0) return;
        if (locations.length === 1) {
            map.setView([locations[0].lat, locations[0].lng], 13);
            return;
        }
        const bounds = locations.map((l) => [l.lat, l.lng]);
        try {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
        } catch {
            /* noop */
        }
    }, [locations, map]);
    return null;
}

export default function OndeEstamos() {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const prev = document.title;
        document.title = "Onde Estamos — Sabor Líquido";
        return () => {
            document.title = prev;
        };
    }, []);
    const [filterCity, setFilterCity] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [filterLine, setFilterLine] = useState("all");
    const [activeId, setActiveId] = useState(null);
    const [flyTo, setFlyTo] = useState(null);
    const markerRefs = useRef({});

    // Initial load
    useEffect(() => {
        let alive = true;
        setLoading(true);
        apiClient
            .get("/locations")
            .then((r) => {
                if (alive) setLocations(r.data || []);
            })
            .catch(() => {
                if (alive) setLocations([]);
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, []);

    const cities = useMemo(() => {
        const set = new Set(locations.map((l) => l.city).filter(Boolean));
        return Array.from(set).sort();
    }, [locations]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return locations.filter((l) => {
            if (filterCity !== "all" && l.city !== filterCity) return false;
            if (filterType !== "all" && l.type !== filterType) return false;
            if (filterLine !== "all" && !(l.lines || []).includes(filterLine))
                return false;
            if (
                q &&
                !`${l.name} ${l.address} ${l.city}`.toLowerCase().includes(q)
            )
                return false;
            return true;
        });
    }, [locations, search, filterCity, filterType, filterLine]);

    const clearFilters = () => {
        setSearch("");
        setFilterCity("all");
        setFilterType("all");
        setFilterLine("all");
    };

    const focusLocation = (loc) => {
        setActiveId(loc.id);
        setFlyTo({ lat: loc.lat, lng: loc.lng, ts: Date.now() });
        const ref = markerRefs.current[loc.id];
        if (ref) {
            // Slight delay so flyTo finishes & popup renders correctly
            setTimeout(() => ref.openPopup(), 750);
        }
    };

    return (
        <div
            className="min-h-screen bg-brand-cream flex flex-col"
            data-testid="onde-estamos-page"
        >
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden pt-12 pb-10 md:pt-20 md:pb-14">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{
                        backgroundImage: "url('/brand/pattern-1.png')",
                        backgroundSize: "260px",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white shadow-soft px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-green ring-1 ring-brand-green/15">
                        <Compass className="h-3.5 w-3.5" /> Onde Estamos
                    </div>
                    <h1 className="mt-5 font-display font-medium text-4xl md:text-6xl text-brand-ink leading-[1.05] max-w-3xl">
                        Encontra o Sabor Líquido{" "}
                        <span className="text-brand-green">perto de ti</span>.
                    </h1>
                    <p className="mt-4 text-base md:text-lg text-brand-ink-soft max-w-2xl">
                        {loading
                            ? "A carregar pontos de venda…"
                            : `${locations.length} ${locations.length === 1 ? "ponto disponível" : "pontos disponíveis"} em Portugal continental, ilhas e além-fronteiras.`}
                    </p>
                </div>
            </section>

            {/* Filters bar */}
            <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Pesquisar por nome, morada ou cidade…"
                            data-testid="onde-search"
                            className="w-full rounded-full bg-brand-cream/60 border-2 border-transparent focus:border-brand-green focus:bg-white px-10 py-2.5 text-sm font-medium text-brand-ink placeholder:text-brand-ink-soft focus:outline-none transition-colors"
                        />
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-ink-soft">
                            <Filter className="h-3.5 w-3.5" />
                        </span>

                        <Pill
                            value={filterCity}
                            onChange={setFilterCity}
                            options={[
                                { v: "all", l: "Todas as cidades" },
                                ...cities.map((c) => ({ v: c, l: c })),
                            ]}
                            testid="onde-filter-city"
                        />
                        <Pill
                            value={filterType}
                            onChange={setFilterType}
                            options={[
                                { v: "all", l: "Todos os tipos" },
                                ...LOCATION_TYPE_OPTIONS.map((o) => ({
                                    v: o.value,
                                    l: `${o.emoji} ${o.label}`,
                                })),
                            ]}
                            testid="onde-filter-type"
                        />
                        <Pill
                            value={filterLine}
                            onChange={setFilterLine}
                            options={[
                                { v: "all", l: "Todas as linhas" },
                                ...LINE_OPTIONS.map((o) => ({
                                    v: o.value,
                                    l: o.label,
                                })),
                            ]}
                            testid="onde-filter-line"
                        />

                        {(search ||
                            filterCity !== "all" ||
                            filterType !== "all" ||
                            filterLine !== "all") && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                data-testid="onde-clear-filters"
                                className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors"
                            >
                                <XCircle className="h-3.5 w-3.5" /> Limpar
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Map + sidebar */}
            <section className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-6 pb-16">
                <div className="grid lg:grid-cols-[420px_1fr] gap-6">
                    {/* Sidebar list */}
                    <aside
                        className="order-2 lg:order-1 rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden flex flex-col"
                        data-testid="onde-list"
                    >
                        <div className="px-5 py-4 border-b border-brand-green/10 flex items-center justify-between">
                            <h2 className="font-display font-medium text-brand-ink">
                                Pontos de venda{" "}
                                <span className="text-brand-ink-soft text-sm font-medium">
                                    ({filtered.length})
                                </span>
                            </h2>
                        </div>

                        {loading ? (
                            <div className="p-12 flex justify-center">
                                <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="p-10 text-center">
                                <MapPin className="h-10 w-10 text-brand-green/40 mx-auto" />
                                <p className="mt-4 text-sm text-brand-ink-soft">
                                    {locations.length === 0
                                        ? "Ainda não há pontos de venda registados."
                                        : "Nenhum ponto corresponde aos filtros."}
                                </p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-brand-green/10 max-h-[640px] overflow-y-auto">
                                {filtered.map((loc) => (
                                    <li
                                        key={loc.id}
                                        data-testid={`onde-item-${loc.id}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => focusLocation(loc)}
                                            className={`w-full text-left px-5 py-4 transition-colors ${
                                                activeId === loc.id
                                                    ? "bg-brand-green-soft"
                                                    : "hover:bg-brand-cream/50"
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="h-9 w-9 rounded-full bg-brand-green-soft text-brand-green flex items-center justify-center text-base shrink-0">
                                                    {LOCATION_TYPE_META[loc.type]
                                                        ?.emoji || "📍"}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-bold text-brand-ink leading-tight">
                                                        {loc.name}
                                                    </div>
                                                    <div className="text-xs text-brand-ink-soft mt-0.5">
                                                        {loc.address} ·{" "}
                                                        <span className="font-semibold text-brand-ink">
                                                            {loc.city}
                                                        </span>
                                                    </div>
                                                    {loc.lines &&
                                                        loc.lines.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                                {loc.lines.map(
                                                                    (l) => (
                                                                        <span
                                                                            key={l}
                                                                            className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${LINE_META[l]?.color || "bg-brand-cream text-brand-ink"}`}
                                                                        >
                                                                            {LINE_META[
                                                                                l
                                                                            ]
                                                                                ?.label ||
                                                                                l}
                                                                        </span>
                                                                    ),
                                                                )}
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </aside>

                    {/* Map */}
                    <div
                        className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-soft ring-1 ring-brand-green/10 bg-white relative"
                        style={{ minHeight: 480, height: "70vh" }}
                        data-testid="onde-map-wrapper"
                    >
                        <MapContainer
                            center={DEFAULT_CENTER}
                            zoom={DEFAULT_ZOOM}
                            scrollWheelZoom
                            style={{ height: "100%", width: "100%" }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <FitToBounds locations={filtered} />
                            <MapController flyTo={flyTo} />
                            {filtered.map((loc) => (
                                <Marker
                                    key={loc.id}
                                    position={[loc.lat, loc.lng]}
                                    icon={brandIcon(
                                        activeId === loc.id ? "#B95C50" : "#7A8B76",
                                        activeId === loc.id ? 1.15 : 1,
                                    )}
                                    eventHandlers={{
                                        click: () => setActiveId(loc.id),
                                    }}
                                    ref={(ref) => {
                                        if (ref) markerRefs.current[loc.id] = ref;
                                    }}
                                >
                                    <Popup minWidth={240} maxWidth={300}>
                                        <PopupCard loc={loc} />
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>

                        {filtered.length === 0 && !loading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm pointer-events-none">
                                <div className="text-center">
                                    <MapPin className="h-10 w-10 text-brand-green/50 mx-auto" />
                                    <p className="mt-3 text-sm font-semibold text-brand-ink">
                                        Sem pontos para mostrar.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
            <WhatsAppButton />
        </div>
    );
}

// ---------- subcomponents ----------

const Pill = ({ value, onChange, options, testid }) => (
    <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="rounded-full bg-brand-cream/60 border-2 border-transparent focus:border-brand-green focus:bg-white px-4 py-2 text-xs font-semibold text-brand-ink focus:outline-none transition-colors cursor-pointer"
    >
        {options.map((o) => (
            <option key={o.v} value={o.v}>
                {o.l}
            </option>
        ))}
    </select>
);

const PopupCard = ({ loc }) => {
    const meta = LOCATION_TYPE_META[loc.type] || LOCATION_TYPE_META.outro;
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`;
    return (
        <div className="text-brand-ink" style={{ fontFamily: "Jost, sans-serif" }}>
            <div className="flex items-center gap-2">
                <span className="text-xl">{meta.emoji}</span>
                <span className="text-[10px] uppercase tracking-widest font-bold text-brand-green">
                    {meta.label}
                </span>
            </div>
            <div
                className="mt-1 font-extrabold text-base leading-tight"
                style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
                {loc.name}
            </div>
            <div className="mt-2 text-xs text-brand-ink-soft leading-snug">
                {loc.address}
                <br />
                <span className="font-semibold text-brand-ink">
                    {[loc.postal_code, loc.city].filter(Boolean).join(" ")}
                </span>
            </div>
            {loc.hours && (
                <div className="mt-2 text-xs flex items-start gap-1.5">
                    <Clock className="h-3 w-3 mt-0.5 shrink-0 text-brand-green" />
                    <span>{loc.hours}</span>
                </div>
            )}
            {loc.phone && (
                <div className="mt-1 text-xs flex items-center gap-1.5">
                    <Phone className="h-3 w-3 shrink-0 text-brand-green" />
                    <a
                        href={`tel:${loc.phone}`}
                        className="text-brand-ink hover:text-brand-green"
                    >
                        {loc.phone}
                    </a>
                </div>
            )}
            {loc.website && (
                <div className="mt-1 text-xs flex items-center gap-1.5">
                    <Globe className="h-3 w-3 shrink-0 text-brand-green" />
                    <a
                        href={loc.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-green hover:underline truncate"
                    >
                        {loc.website.replace(/^https?:\/\//, "")}
                    </a>
                </div>
            )}
            {loc.lines && loc.lines.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {loc.lines.map((l) => (
                        <span
                            key={l}
                            className={`text-[9px] uppercase tracking-widest font-bold rounded-full px-1.5 py-0.5 ${LINE_META[l]?.color || ""}`}
                        >
                            {LINE_META[l]?.label || l}
                        </span>
                    ))}
                </div>
            )}
            {loc.notes && (
                <p className="mt-2 text-[11px] text-brand-ink-soft italic">
                    {loc.notes}
                </p>
            )}
            <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-3 py-1.5 text-[11px] font-bold transition-colors"
            >
                <Compass className="h-3 w-3" /> Como chegar
            </a>
        </div>
    );
};
